// enquetes.tsx - Módulo Isolado de Enquetes (IBIRACO)
import React, { useEffect, useState } from 'react';
import { db, auth } from './firebaseConfig';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  serverTimestamp, 
  deleteDoc, 
  doc, 
  updateDoc 
} from 'firebase/firestore';
import { 
  Users, 
  Plus, 
  XCircle, 
  CheckCircle, 
  Trash2,
  ArrowLeft,
  Megaphone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// 🔧 TIPOS
// ============================================================================

type Enquete = {
  id: string;
  pergunta: string;
  opcoes: { id: string; texto: string; votos: number }[];
  cidade: string;
  dataCriacao: any;
  dataFim?: any;
  ativa: boolean;
  totalVotos: number;
};

// ============================================================================
// 🔧 FUNÇÕES UTILITÁRIAS
// ============================================================================

const normalizeCidade = (cidade: string): string => {
  return cidade
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");
};

// ============================================================================
// 🖥️ COMPONENTE PRINCIPAL
// ============================================================================

export default function EnquetesScreen() {
  const navigate = useNavigate();
  
  // Estados da cidade do admin
  const [cidadeDisplay, setCidadeDisplay] = useState<string>('');
  const [cidadeNormalizada, setCidadeNormalizada] = useState<string>('');
  
  // Estados de enquetes
  const [enquetes, setEnquetes] = useState<Enquete[]>([]);
  const [novaEnquete, setNovaEnquete] = useState({ 
    pergunta: '', 
    opcoes: ['',''] as string[], 
    dataFim: '' 
  });
  const [abaAtiva, setAbaAtiva] = useState<'criar' | 'listar'>('criar');
  const [loading, setLoading] = useState(true);

  // ============================================================================
  // 🔄 INICIALIZAÇÃO - Buscar cidade do admin logado
  // ============================================================================
  
  useEffect(() => {
    const cidadeDisplayLS = localStorage.getItem('cidadeDisplay');
    const cidadeNormalizadaLS = localStorage.getItem('cidadeNormalizada');
    
    if (cidadeDisplayLS && cidadeNormalizadaLS) {
      setCidadeDisplay(cidadeDisplayLS);
      setCidadeNormalizada(cidadeNormalizadaLS);
      setLoading(false);
    } else {
      // Fallback: inferir do email do usuário
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          const email = user.email || '';
          if (email.includes('mariocampos')) {
            setCidadeDisplay('Mário Campos');
            setCidadeNormalizada('mario_campos');
          } else if (email.includes('sarzedo')) {
            setCidadeDisplay('Sarzedo');
            setCidadeNormalizada('sarzedo');
          } else {
            setCidadeDisplay('Ibirité');
            setCidadeNormalizada('ibirite');
          }
          setLoading(false);
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // ============================================================================
  // 🔄 MONITORAR ENQUETES DA CIDADE
  // ============================================================================
  
  useEffect(() => {
    if (!cidadeNormalizada) return;

    const q = query(
      collection(db, "enquetes"), 
      where("cidade", "==", cidadeNormalizada)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Enquete));
      
      // Ordenar por data (mais recente primeiro)
      const dataOrdenada = data.sort((a, b) => {
        const dateA = a.dataCriacao?.seconds || 0;
        const dateB = b.dataCriacao?.seconds || 0;
        return dateB - dateA;
      });
      
      setEnquetes(dataOrdenada);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [cidadeNormalizada]);

  // ============================================================================
  // 🎯 FUNÇÕES DE ENQUETES
  // ============================================================================

  const adicionarOpcao = () => {
    if (novaEnquete.opcoes.length < 5) {
      setNovaEnquete(prev => ({ ...prev, opcoes: [...prev.opcoes, ''] }));
    }
  };

  const removerOpcao = (index: number) => {
    if (novaEnquete.opcoes.length > 2) {
      setNovaEnquete(prev => ({ 
        ...prev, 
        opcoes: prev.opcoes.filter((_, i) => i !== index) 
      }));
    }
  };

  const criarEnquete = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!novaEnquete.pergunta || novaEnquete.opcoes.filter(o => o.trim()).length < 2) {
      return alert("Preencha a pergunta e pelo menos 2 opções!");
    }
    
    try {
      const opcoesFormatadas = novaEnquete.opcoes
        .filter(o => o.trim())
        .map((texto, i) => ({ 
          id: `opt${i+1}`, 
          texto: texto.trim(), 
          votos: 0 
        }));
      
      // 1️⃣ Criar a enquete
      const enqueteDoc = await addDoc(collection(db, "enquetes"), {
        pergunta: novaEnquete.pergunta.trim(),
        opcoes: opcoesFormatadas,
        cidade: cidadeNormalizada,
        dataCriacao: serverTimestamp(),
        dataFim: novaEnquete.dataFim ? new Date(novaEnquete.dataFim) : null,
        ativa: true,
        totalVotos: 0
      });
      
      // 2️⃣ Criar notificação automática (expira em 48h)
      const dataExpiracao = new Date();
      dataExpiracao.setHours(dataExpiracao.getHours() + 48);
      
      await addDoc(collection(db, "notificacoes"), {
        titulo: 'Nova Enquete Disponível! 📊',
        mensagem: `A prefeitura criou uma nova enquete: "${novaEnquete.pergunta}"`,
        tipo: 'enquete',
        cidade: cidadeNormalizada,
        dataCriacao: serverTimestamp(),
        dataExpiracao: dataExpiracao,
        lida: false,
        linkAcao: enqueteDoc.id,
        emitidoPor: cidadeDisplay
      });
      
      // Resetar formulário
      setNovaEnquete({ pergunta: '', opcoes: ['',''], dataFim: '' });
      setAbaAtiva('listar');
      alert("Enquete criada com sucesso e notificação enviada!");
      
    } catch (err) {
      console.error(err);
      alert("Erro ao criar enquete.");
    }
  };

  const toggleEnquete = async (id: string, ativaAtual: boolean) => {
    try {
      await updateDoc(doc(db, "enquetes", id), { 
        ativa: !ativaAtual 
      });
    } catch (err) {
      console.error(err);
      alert("Erro ao atualizar enquete.");
    }
  };

  const apagarEnquete = async (id: string) => {
    if (window.confirm("Deseja realmente excluir esta enquete?")) {
      try {
        await deleteDoc(doc(db, "enquetes", id));
      } catch (err) {
        console.error(err);
        alert("Erro ao excluir enquete.");
      }
    }
  };

  // ============================================================================
  // 🎨 RENDERIZAÇÃO
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F7FE] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#e67e22] mx-auto mb-4"></div>
          <p className="text-gray-600 font-bold">Carregando enquetes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FE] p-4 md:p-8">
      {/* HEADER */}
      <div className="max-w-5xl mx-auto mb-6">
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#e67e22] transition-all mb-4 font-bold"
        >
          <ArrowLeft size={20} /> Voltar para o Dashboard
        </button>
        
        <div className="bg-white p-6 rounded-[2.5rem] shadow-sm flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-[#e67e22] p-3 rounded-2xl">
              <Megaphone size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-[#1A1C1D]">Enquetes</h1>
              <p className="text-sm text-gray-500 font-bold">Prefeitura de {cidadeDisplay}</p>
            </div>
          </div>
        </div>
      </div>

      {/* CONTEÚDO PRINCIPAL */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-[3rem] shadow-sm border border-gray-100 overflow-hidden">
          
          {/* TABS */}
          <div className="flex bg-gray-50/50 p-2 gap-2">
            <button 
              onClick={() => setAbaAtiva('criar')}
              className={`flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-tighter transition-all ${
                abaAtiva === 'criar' 
                  ? 'bg-[#e67e22] text-white shadow-lg' 
                  : 'text-gray-400 hover:bg-gray-100'
              }`}
            >
              <Plus size={14} className="inline mr-2" /> Criar Enquete
            </button>
            <button 
              onClick={() => setAbaAtiva('listar')}
              className={`flex-1 py-3 rounded-2xl font-black text-xs uppercase tracking-tighter transition-all ${
                abaAtiva === 'listar' 
                  ? 'bg-[#e67e22] text-white shadow-lg' 
                  : 'text-gray-400 hover:bg-gray-100'
              }`}
            >
              <Users size={14} className="inline mr-2" /> Enquetes Ativas ({enquetes.filter(e => e.ativa).length})
            </button>
          </div>

          <div className="p-6">
            {/* ABA: CRIAR ENQUETE */}
            {abaAtiva === 'criar' && (
              <form onSubmit={criarEnquete} className="space-y-6">
                <div>
                  <label className="block text-sm font-black text-gray-700 uppercase mb-2">
                    Pergunta da Enquete *
                  </label>
                  <input 
                    value={novaEnquete.pergunta}
                    onChange={e => setNovaEnquete(prev => ({ ...prev, pergunta: e.target.value }))}
                    placeholder="Ex: Qual bairro precisa de mais iluminação?"
                    className="w-full p-4 bg-gray-50 rounded-2xl font-bold text-sm outline-none border-2 border-transparent focus:border-orange-200 transition-all"
                    required
                  />
                </div>

                {/* OPÇÕES */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-black text-gray-700 uppercase">
                      Opções de Resposta *
                    </label>
                    <span className="text-[10px] text-gray-400 font-bold">
                      {novaEnquete.opcoes.length}/5 opções
                    </span>
                  </div>
                  
                  {novaEnquete.opcoes.map((opcao, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-1 relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-black text-xs">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <input 
                          value={opcao}
                          onChange={e => {
                            const novas = [...novaEnquete.opcoes];
                            novas[index] = e.target.value;
                            setNovaEnquete(prev => ({ ...prev, opcoes: novas }));
                          }}
                          placeholder={`Opção ${index + 1}`}
                          className="w-full p-4 pl-10 bg-gray-50 rounded-2xl font-bold text-sm outline-none border-2 border-transparent focus:border-orange-200 transition-all"
                          required
                        />
                      </div>
                      {novaEnquete.opcoes.length > 2 && (
                        <button 
                          type="button"
                          onClick={() => removerOpcao(index)}
                          className="p-4 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-2xl transition-all"
                        >
                          <XCircle size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                  
                  {novaEnquete.opcoes.length < 5 && (
                    <button 
                      type="button"
                      onClick={adicionarOpcao}
                      className="text-[10px] font-black text-[#e67e22] uppercase hover:underline flex items-center gap-1"
                    >
                      <Plus size={12} /> Adicionar mais uma opção
                    </button>
                  )}
                </div>

                {/* DATA DE EXPIRAÇÃO */}
                <div>
                  <label className="block text-sm font-black text-gray-700 uppercase mb-2">
                    Expirar em (opcional)
                  </label>
                  <input 
                    type="date"
                    value={novaEnquete.dataFim}
                    onChange={e => setNovaEnquete(prev => ({ ...prev, dataFim: e.target.value }))}
                    className="w-full p-4 bg-gray-50 rounded-2xl font-bold text-sm outline-none border-2 border-transparent focus:border-orange-200 transition-all"
                  />
                  <p className="text-[10px] text-gray-400 mt-2 font-bold">
                    Se não definir, a enquete ficará ativa indefinidamente
                  </p>
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-[#e67e22] text-white py-4 rounded-2xl font-black uppercase text-sm tracking-widest shadow-lg hover:bg-orange-600 transition-all"
                >
                  Publicar Enquete e Notificar Cidadãos
                </button>
              </form>
            )}

            {/* ABA: LISTAR ENQUETES */}
            {abaAtiva === 'listar' && (
              <div className="space-y-4">
                {enquetes.length > 0 ? (
                  enquetes.map(enq => (
                    <div 
                      key={enq.id} 
                      className={`p-6 rounded-2xl border-2 transition-all ${
                        enq.ativa 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-gray-50 border-gray-200 opacity-75'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="font-black text-lg text-gray-800 mb-2">
                            {enq.pergunta}
                          </h3>
                          <div className="flex items-center gap-3 text-[10px] text-gray-500 font-bold uppercase">
                            <span>Total: {enq.totalVotos} votos</span>
                            <span>•</span>
                            <span>{enq.opcoes.length} opções</span>
                            {enq.dataFim && (
                              <>
                                <span>•</span>
                                <span>Expira em: {new Date(enq.dataFim).toLocaleDateString('pt-BR')}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <button 
                          onClick={() => toggleEnquete(enq.id, enq.ativa)}
                          className={`p-3 rounded-xl transition-all ${
                            enq.ativa 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                          }`}
                        >
                          {enq.ativa ? <CheckCircle size={20} /> : <XCircle size={20} />}
                        </button>
                      </div>

                      {/* OPÇÕES E VOTOS */}
                      <div className="space-y-2 mb-4">
                        {enq.opcoes.map(opt => {
                          const percentual = enq.totalVotos > 0 
                            ? Math.round((opt.votos / enq.totalVotos) * 100) 
                            : 0;
                          
                          return (
                            <div key={opt.id} className="relative">
                              <div className="flex justify-between text-xs mb-1">
                                <span className="font-bold text-gray-700">{opt.texto}</span>
                                <span className="font-black text-[#e67e22]">
                                  {opt.votos} votos ({percentual}%)
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-[#e67e22] h-2 rounded-full transition-all"
                                  style={{ width: `${percentual}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex justify-end">
                        <button 
                          onClick={() => apagarEnquete(enq.id)}
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 px-4 py-2 rounded-xl transition-all flex items-center gap-2 text-xs font-black uppercase"
                        >
                          <Trash2 size={14} /> Excluir Enquete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Megaphone size={64} className="mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-black text-gray-800 mb-2">
                      Nenhuma enquete criada
                    </h3>
                    <p className="text-gray-500 text-sm mb-6">
                      Crie sua primeira enquete para consultar a opinião dos cidadãos
                    </p>
                    <button 
                      onClick={() => setAbaAtiva('criar')}
                      className="bg-[#e67e22] text-white px-6 py-3 rounded-2xl font-black uppercase text-xs hover:bg-orange-600 transition-all"
                    >
                      Criar Enquete Agora
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}